import { groupBy } from 'lodash';

import { TimesheetDto, UserCoreDto } from 'src/api/FiretrolApiClient';
import { splitHoursByOvertime } from 'src/app/timesheets/splitHoursByOvertime';

export interface GroupedTimesheetRow {
  id: string;
  userId: number;
  userName: string;
  user: UserCoreDto;
  regularHours: number;
  overtimeHours: number;
  timesheetCount: number;
  subRows: TimesheetDto[];
}

const GROUP_ROW_ID_PREFIX = 'user-';

export const groupRowId = (userId: number | string): string =>
  `${GROUP_ROW_ID_PREFIX}${userId}`;

/** The user id behind a grouped row id; null for any other row id. */
export const userIdFromGroupRowId = (rowId: string): number | null => {
  if (!rowId.startsWith(GROUP_ROW_ID_PREFIX)) return null;
  const userId = Number(rowId.slice(GROUP_ROW_ID_PREFIX.length));
  return Number.isInteger(userId) && userId > 0 ? userId : null;
};

export const groupTimesheetsByUser = (
  timesheets: TimesheetDto[],
): GroupedTimesheetRow[] => {
  const grouped = groupBy(timesheets, (ts) => ts.userId);

  return Object.entries(grouped).map(([userId, userTimesheets]) => {
    const firstTimesheet = userTimesheets[0];
    const user = firstTimesheet.user;

    const { regularHours, overtimeHours } = userTimesheets.reduce(
      (acc, ts) => {
        const split = splitHoursByOvertime(ts.rows);
        acc.regularHours += split.regularHours;
        acc.overtimeHours += split.overtimeHours;
        return acc;
      },
      { regularHours: 0, overtimeHours: 0 },
    );

    return {
      id: groupRowId(userId),
      userId: Number(userId),
      userName: user?.fullName || 'Unknown User',
      user: user || ({} as UserCoreDto),
      regularHours,
      overtimeHours,
      timesheetCount: userTimesheets.length,
      subRows: userTimesheets,
    };
  });
};
